import React, { useEffect } from 'react'
import { Formik } from 'formik'
import { Dispatch } from 'redux'
import { Form, FastField } from 'formik'
import { ICapabilities } from '../../types/capabilties'
import { IGroupDetailed } from '../../types/group'
import { updateGroup } from '../../actions/group'
import { getCapabilities } from '../../actions/capabilities'
import { push } from '../../actions/router'
import { unwrapId, wrapId } from '../../services/common'
import { colors } from '../../styles'
import { DetailsTemplate, Space } from '../../layouts'
import {
  P,
  H2,
  Button,
  Link,
  NavigationGuard,
  CustomCheckboxGroupField,
} from '../../components'
import { RequiredApprovalCapabilityBlock } from './RequiredApprovalCapabilityBlock'

export interface IUpdateGroupPublishingCapabilitiesForm {
  group: IGroupDetailed
  capabilities: ICapabilities
  editable: boolean
  canCreateCapability: boolean
  dispatch: Dispatch
}

export const UpdateGroupPublishingCapabilitiesForm = ({
  group,
  capabilities,
  editable = true,
  canCreateCapability,
  dispatch,
}: IUpdateGroupPublishingCapabilitiesForm) => {
  useEffect(() => {
    if (!capabilities.list.length) {
      dispatch(getCapabilities())
    }
  }, [dispatch, capabilities.list.length])
  const initialValues = { capabilities: group.capabilities.map(unwrapId) }
  return !group.isLoading ? (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => {
        dispatch(
          updateGroup(
            {
              ...group,
              capabilities: values.capabilities.map(wrapId),
            },
            initialValues.capabilities
          )
        )
      }}
      render={(formikProps) => (
        <Form>
          <DetailsTemplate
            aside={
              <>
                <H2 mb={4}>Save changes</H2>
                <P mb={2}>
                  Changes that require approval will be saved and take effect
                  automatically once the approval granted. Any other changes —
                  right after you hit save.
                </P>
                {canCreateCapability && (
                  <P mb={6}>
                    To add new group capabilities go to{' '}
                    <Link
                      to="/settings/capabilities?tab=create-new"
                      invisible="true"
                      color={colors.portal}
                    >
                      Group Capabilities section
                    </Link>
                  </P>
                )}
                <Button
                  id="submit-publishing-group-capabilities"
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={!formikProps.isValid || !editable}
                >
                  Save
                </Button>
                <Space pt={4}>
                  <Button
                    id="update-capabilities-cancel-button"
                    fullWidth
                    onClick={() => {
                      dispatch(push('/settings/groups?tab=groups'))
                    }}
                  >
                    Cancel
                  </Button>
                </Space>
              </>
            }
          >
            <H2 mb={4}>Select group capabilities</H2>
            {capabilities.list.map(
              ({ id, title, description, requiresApproval }) => (
                <div key={id}>
                  <FastField
                    name="capabilities"
                    fieldValue={id}
                    label={
                      <H2>
                        {title}
                        {requiresApproval && (
                          <RequiredApprovalCapabilityBlock
                            id={id}
                            group={group}
                          />
                        )}
                      </H2>
                    }
                    component={CustomCheckboxGroupField}
                    disabled={!editable}
                  />
                  <P style={{ wordBreak: 'break-all' }}>{description}</P>
                </div>
              )
            )}
          </DetailsTemplate>
          <NavigationGuard isBlocking={formikProps.dirty} />
        </Form>
      )}
      validationSchema={{}}
    />
  ) : null
}
